/**
 * 认证服务
 * 处理用户登录、注册等 API 调用
 */
import apiClient from './apiClient';
import logger from '../utils/logger';
import storage from '../utils/LocalStorageManager';

// 引入同步服务
import syncAllDataToServer from './syncService';

// 本地存储键名
export const AUTH_TOKEN_KEY = 'authToken';
export const USER_INFO_KEY = 'userInfo';
export const DATA_SYNCED_KEY = 'dataSynced';
export const SYNC_IN_PROGRESS_KEY = 'syncInProgress';

const authLogger = logger.withPrefix('认证服务');

/**
 * 用户登录
 * @param {Object} credentials 包含 email 和 password 的对象
 * @returns {Promise} 包含用户信息和令牌的 Promise
 */
export const login = async (credentials) => {
  return apiClient.post('/auth/login', credentials);
};

/**
 * 用户登录并同步数据
 * @param {Object} credentials 包含 email 和 password 的对象
 * @param {Function} dispatch Redux dispatch 函数
 * @returns {Promise} 包含用户信息和令牌的 Promise
 */
export const loginAndSyncData = async (credentials, dispatch) => {
  try {
    // 登录
    const response = await login(credentials);
    
    // 保存认证信息
    if (response && response.token) {
      saveAuthData(response.user, response.token);
      
      // 双向同步数据
      await syncAllDataToServer(dispatch);
      
      // 记录已同步标记
      markDataSynced();
    }
    
    return response;
  } catch (error) {
    authLogger.error('登录或同步数据失败:', error);
    throw error;
  }
};

/**
 * 用户注册
 * @param {Object} userData 用户注册数据（用户名、邮箱、密码等）
 * @returns {Promise} 包含用户信息和令牌的 Promise
 */
export const register = async (userData) => {
  return apiClient.post('/auth/register', userData);
};

/**
 * 用户注册并同步数据
 * @param {Object} userData 用户注册数据
 * @param {Function} dispatch Redux dispatch 函数
 * @returns {Promise} 包含用户信息和令牌的 Promise
 */
export const registerAndSyncData = async (userData, dispatch) => {
  try {
    // 注册
    const response = await register(userData);
    
    // 保存认证信息
    if (response && response.token) {
      saveAuthData(response.user, response.token);
      
      // 双向同步数据
      await syncAllDataToServer(dispatch);
      
      // 记录已同步标记
      markDataSynced();
    }
    
    return response;
  } catch (error) {
    authLogger.error('注册或同步数据失败:', error);
    throw error;
  }
};

/**
 * 获取当前用户信息
 * @param {string} token JWT 令牌
 * @returns {Promise} 包含用户信息的 Promise
 */
export const getUserProfile = async (token) => {
  return apiClient.get('/auth/me', { token });
};

/**
 * 发送验证码到指定邮箱
 * @param {string} email 邮箱地址
 * @returns {Promise} 包含发送结果的 Promise
 */
export const sendVerificationCode = async (email) => {
  return apiClient.post('/auth/send-code', { email });
};

/**
 * 验证邮箱验证码
 * @param {string} email 邮箱地址
 * @param {string} code 验证码
 * @returns {Promise} 包含验证结果的 Promise
 */
export const verifyEmailCode = async (email, code) => {
  return apiClient.post('/auth/verify-code', { email, code });
};

/**
 * 重置密码
 * @param {string} email 邮箱地址
 * @param {string} code 验证码
 * @param {string} password 新密码
 * @returns {Promise} 包含重置结果的 Promise
 */
export const resetPassword = async (email, code, password) => {
  return apiClient.post('/auth/reset-password', { email, code, password });
};

/**
 * 保存认证信息到本地存储
 * @param {Object} user 用户信息
 * @param {string} token JWT 令牌
 */
export const saveAuthData = (user, token) => {
  storage.setAuthToken(token);
  storage.setUserInfo(user);
  
  // 触发全局事件
  window.dispatchEvent(new Event('authStatusChanged'));
};

/**
 * 从本地存储移除认证信息
 */
export const removeAuthData = () => {
  storage.removeItem(AUTH_TOKEN_KEY);
  storage.removeItem(USER_INFO_KEY);
  storage.removeItem(DATA_SYNCED_KEY);
  
  // 触发全局事件
  window.dispatchEvent(new Event('authStatusChanged'));
};

/**
 * 从本地存储获取认证信息
 * @returns {Object|null} 包含用户信息和令牌的对象，如果不存在则返回 null
 */
export const getAuthDataFromStorage = () => {
  try {
    const token = storage.getAuthToken();
    const userInfo = storage.getUserInfo(); // 已经是解析后的对象
    
    if (token && userInfo) {
      return { user: userInfo, token };
    }
    
    return null;
  } catch (error) {
    logger.error('获取认证数据失败:', error);
    removeAuthData();
    return null;
  }
};

/**
 * 检查数据是否已同步
 * @returns {boolean} 数据是否已同步
 */
export const isDataSynced = () => {
  return storage.isDataSynced();
};

/**
 * 检查是否正在同步
 * @returns {boolean} 是否正在同步
 */
export const isSyncInProgress = () => {
  return storage.getItem(SYNC_IN_PROGRESS_KEY) === 'true';
};

/**
 * 开始同步
 */
export const startSync = () => {
  storage.setItem(SYNC_IN_PROGRESS_KEY, 'true');
};

/**
 * 获取认证token
 * @returns {string} 认证token
 */
export const getAuthToken = () => {
  return storage.getAuthToken();
};

/**
 * 结束同步
 */
export const endSync = () => {
  storage.removeItem(SYNC_IN_PROGRESS_KEY);
};

/**
 * 标记同步失败
 */
export const markSyncFailed = () => {
  storage.removeItem(SYNC_IN_PROGRESS_KEY);
  storage.removeItem(DATA_SYNCED_KEY);
};

/**
 * 标记同步成功
 */
export const markSyncSuccess = () => {
  storage.setDataSynced(true);
};

/**
 * 标记数据已同步
 */
export const markDataSynced = () => {
  storage.setDataSynced(true);
};

/**
 * 标记数据已同步（用于导入）
 */
export const markImportDataSynced = () => {
  storage.setDataSynced(true);
};

/**
 * 同步数据
 * 双向同步本地和服务端数据
 * @param {Function} dispatch Redux dispatch 函数
 * @returns {Promise<{success: boolean, message: string}>} 同步结果
 */
export const syncData = async (dispatch) => {
  authLogger.log('syncData 开始执行');
  
  // 检查是否正在同步中
  if (isSyncInProgress()) {
    authLogger.log('同步正在进行中，跳过本次同步');
    return {
      success: false,
      message: '同步正在进行中，请稍后再试'
    };
  }

  // 设置同步状态
  try {
    startSync();
  } catch (error) {
    authLogger.warn('设置同步状态失败:', error);
  }

  try {
    // 检查是否已登录
    const token = getAuthToken();
    if (!token) {
      authLogger.log('用户未登录，同步中止');
      endSync();
      return {
        success: false,
        message: '请先登录以同步数据'
      };
    }
    
    authLogger.log('用户已登录，开始同步数据');
    
    // 调用同步服务的同步方法
    let syncResult;
    try {
      authLogger.log('调用 syncAllDataToServer 方法');
      syncResult = await syncAllDataToServer(dispatch);
      authLogger.log('syncAllDataToServer 执行完成, 结果:', syncResult);
      
      if (!syncResult || !syncResult.success) {
        throw new Error(syncResult?.message || '同步失败');
      }
    } catch (syncError) {
      authLogger.error('同步数据失败:', syncError);
      // 清理同步状态
      endSync();
      markSyncFailed();
      return {
        success: false,
        message: '同步过程中出现错误，部分数据可能未同步'
      };
    }
    
    // 更新同步状态
    try {
      markSyncSuccess();
      authLogger.log('已更新同步状态标记');
    } catch (storageError) {
      authLogger.warn('更新同步状态失败:', storageError);
    }
    
    // 使用 CustomEvent 的 detail 传递更多信息
    const syncEventDetail = {
      isFromSync: true,
      syncTime: Date.now(),
      syncResult: syncResult
    };
    
    // 只在确实同步成功或者有数据更新时触发页面刷新
    if (syncResult.success || syncResult.hasUpdates) {
      authLogger.log('触发同步完成事件');
      window.dispatchEvent(new CustomEvent('forcePagesRefresh', {
        detail: syncEventDetail
      }));
    }
    
    // 清理同步进行中的状态
    endSync();
    
    return syncResult;
  } catch (error) {
    authLogger.error('同步数据失败:', error);
    // 清理同步状态
    endSync();
    markSyncFailed();
    return {
      success: false,
      message: '同步数据失败，请稍后重试'
    };
  }
};

export default {
  login,
  loginAndSyncData,
  register,
  registerAndSyncData,
  getUserProfile,
  sendVerificationCode,
  verifyEmailCode,
  resetPassword,
  saveAuthData,
  removeAuthData,
  getAuthDataFromStorage,
  isDataSynced,
  isSyncInProgress,
  startSync,
  getAuthToken,
  endSync,
  markSyncFailed,
  markSyncSuccess,
  markDataSynced,
  markImportDataSynced,
  syncData
}; 